import * as React from 'react';
import { IPost } from '../types';

interface IProps {
	page: number;
	maxNumPages: number;
	posts: Array<IPost>;
	loading: boolean;
	setPage?: (page: number) => void;
}

const LoadMore = ({ page, maxNumPages, posts, loading, setPage }: IProps) => {
	const [previousPostCount, setPreviousPostCount] = React.useState<number>(0);
	const wasLoadingRef = React.useRef<boolean>(false);

	// Track when loading state changes from true to false after clicking load more
	React.useEffect(() => {
		if (posts.length > previousPostCount) {
			// Focus on the first newly added post
			const firstNewPostIndex = previousPostCount;
			const firstNewPostElement = document.querySelector<HTMLElement>(
				`.pfa-posts-grid > *:nth-child(${firstNewPostIndex + 1})`
			);

			if (firstNewPostElement) {
				// Find the first focusable element within the post
				const focusableElement = firstNewPostElement.querySelector<HTMLElement>(
					'a, button, [href], [tabindex]:not([tabindex="-1"])'
				);

				if (focusableElement) {
					focusableElement.focus();
				} else {
					// If no focusable element found, make the post container focusable and focus it
					const postContainer = firstNewPostElement;
					postContainer.setAttribute('tabindex', '-1');
					postContainer.focus();
				}
			}

			setPreviousPostCount(posts.length);
		}
	}, [loading, posts.length]);

	if (maxNumPages > page) {
		const handleOnClick = () => {
			if (setPage) {
				// Store the current post count before loading more
				setPreviousPostCount(posts.length);
				setPage(page + 1);
			}
		};

		return (
			<div className="pfa-load-more">
				<button
					className="pfa-load-more__button"
					onClick={handleOnClick}
					disabled={loading}
					aria-live="polite"
					aria-describedby="load-more-description"
				>
					{loading ? 'Loading...' : 'Show More'}
				</button>
				<div id="load-more-description" className="sr-only">
					After clicking Show More, focus will move to the first newly loaded post
				</div>
			</div>
		);
	}

	return <></>;
};

LoadMore.displayName = 'LoadMore';

export default LoadMore;
